import { Checkbox } from '@creditornot/cb-checkbox';
import { InputContainer } from '@creditornot/cb-form';
import { Input } from '@creditornot/cb-input';
import { Select } from '@creditornot/cb-select';
import React, { useMemo, useState } from 'react';
import { Field, Form } from 'react-final-form';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { RFFFieldErrorWrapper } from 'common/components';
import validateYupSchema from 'common/utils/validateYupSchema';
import { useLanguage } from 'locale';

import BusinessAddressFields from './BusinessAddressFields';
import { CountryConfig } from '../../../SelfServeFormView/SelfServeSignUpForm/types';
import { BusinessDetails, BusinessDetailsValidationSchema } from '../validation';

type BusinessDetailsFormProps = {
  country: string;
  initialData: BusinessDetails;
  countryConfigs: CountryConfig[];
  formId: string;
  disabled: boolean;
  handleSubmit: (values: BusinessDetails) => Promise<void | { [key: string]: string | Error }>;
};

const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
`;

const StyledInputContainer = styled(InputContainer)`
  margin-block-end: 16px;
`;

const CityAndPostCode = styled.div`
  margin-block-start: 8px;
  margin-block-end: 16px;
  display: flex;
`;

const PostCode = styled(InputContainer)`
  flex: 0 0 50%;
  margin-inline-end: 8px;
`;

const City = styled(InputContainer)`
  flex: 1 1 auto;
`;

const StyledCheckbox = styled(Checkbox)`
  font-weight: 400;
`;

const BusinessDetailsForm = ({
  handleSubmit,
  formId,
  countryConfigs,
  initialData,
  country,
}: BusinessDetailsFormProps) => {
  const language = useLanguage();
  const { formatMessage } = useIntl();

  const [isSameAddress, setIsSameAddress] = useState(initialData.isSameAddress);

  const cityOptions = useMemo(
    () =>
      countryConfigs
        ?.find(config => config.code === country)
        ?.cities.map(city => {
          const cityNameInEnglish = city.name.find(n => n.lang === 'en')?.value || city.slug;
          const cityLabel = city.name.find(n => n.lang === language.key)?.value || cityNameInEnglish;

          return {
            label: cityLabel,
            value: cityNameInEnglish,
          };
        }) || [],
    [country, countryConfigs, language.key],
  );

  const initialValues: BusinessDetails = useMemo(() => {
    const businessCityNotInTheList = cityOptions.some(cityOption => cityOption.value === initialData.businessCity);
    const initialState = { ...initialData };
    if (initialData.businessCity && !businessCityNotInTheList) {
      initialState.businessOtherCity = initialData.businessCity;
      initialState.businessCity = 'other';
    }
    return initialState;
  }, [cityOptions, initialData]);

  return (
    <Form<BusinessDetails>
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validate={validateYupSchema(BusinessDetailsValidationSchema)}
    >
      {({ handleSubmit }) => (
        <StyledForm onSubmit={handleSubmit} id={formId}>
          <StyledInputContainer
            label={formatMessage({ id: 'self-serve-sign-up.wizard.business-info.restaurant' })}
            labelHtmlFor="onboarding-wizard-form-venue-venues-name"
          >
            <Field<string | undefined> name="name">
              {field => (
                <RFFFieldErrorWrapper field={field}>
                  {input => <Input {...input} id="onboarding-wizard-form-venue-venues-name" />}
                </RFFFieldErrorWrapper>
              )}
            </Field>
          </StyledInputContainer>

          <InputContainer
            label={formatMessage({ id: 'self-serve-sign-up.wizard.business-info.street-address' })}
            labelHtmlFor="onboarding-wizard-form-venue-venues-address"
          >
            <Field<string | undefined> name="address">
              {field => (
                <RFFFieldErrorWrapper field={field}>
                  {input => <Input {...input} id="onboarding-wizard-form-venue-venues-address" />}
                </RFFFieldErrorWrapper>
              )}
            </Field>
          </InputContainer>

          <CityAndPostCode data-testid="self-serve-sign-up-venues-city">
            <PostCode
              label={formatMessage({ id: 'self-serve-sign-up.wizard.business-info.postal-code' })}
              labelHtmlFor="onboarding-wizard-form-venue-venues-postCode"
            >
              <Field<string | undefined> name="postCode">
                {field => (
                  <RFFFieldErrorWrapper field={field}>
                    {input => <Input {...input} id="onboarding-wizard-form-venue-venues-postCode" />}
                  </RFFFieldErrorWrapper>
                )}
              </Field>
            </PostCode>

            <City label={formatMessage({ id: 'self-serve-sign-up.wizard.business-info.city' })}>
              <Field<string | null> name="city">
                {field => (
                  <RFFFieldErrorWrapper field={field}>
                    {input => <Select inputId="city" options={cityOptions} placeholder="" {...input} />}
                  </RFFFieldErrorWrapper>
                )}
              </Field>
            </City>
          </CityAndPostCode>

          <StyledInputContainer
            label={formatMessage({ id: 'self-serve-sign-up.wizard.business-info.business-name' })}
            labelHtmlFor="name"
            description={formatMessage({ id: 'self-serve-sign-up.wizard.business-info.businessNameInfo' })}
          >
            <Field<string | undefined> name="businessName">
              {field => <RFFFieldErrorWrapper field={field}>{input => <Input {...input} />}</RFFFieldErrorWrapper>}
            </Field>
          </StyledInputContainer>

          <StyledInputContainer
            label={formatMessage({ id: 'self-serve-sign-up.wizard.business-info.business-id' })}
            labelHtmlFor="businessId"
          >
            <Field<string | undefined> name="businessId">
              {field => (
                <RFFFieldErrorWrapper field={field}>
                  {input => <Input {...input} id="businessId" />}
                </RFFFieldErrorWrapper>
              )}
            </Field>
          </StyledInputContainer>

          <InputContainer label={formatMessage({ id: 'self-serve-sign-up.wizard.business-info.business-address' })}>
            <Field<string | undefined> name="isSameAddress" type="checkbox">
              {field => (
                <RFFFieldErrorWrapper field={field}>
                  {input => (
                    <StyledCheckbox
                      {...input}
                      checked={isSameAddress}
                      onChange={event => {
                        input.onChange(event.currentTarget.checked);
                        setIsSameAddress(event.currentTarget.checked);
                      }}
                    >
                      {formatMessage({ id: 'self-serve-sign-up.wizard.business-info.same-address' })}
                    </StyledCheckbox>
                  )}
                </RFFFieldErrorWrapper>
              )}
            </Field>
            {!isSameAddress && (
              <BusinessAddressFields cityOptions={cityOptions} initiallySelectedCity={initialValues.businessCity} />
            )}
          </InputContainer>
        </StyledForm>
      )}
    </Form>
  );
};

export default BusinessDetailsForm;
